import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ErrorApi } from '../lib/api'
import { puede } from '../lib/permisos'
import type { DatosPersonal, Grupo, PersonaEnPersonal, RamaPermisos, Usuario } from '../lib/tipos'

type Pestaña = 'usuarios' | 'grupos' | 'oficinas' | 'equipos' | 'vacaciones'

const PESTAÑAS: Array<{ clave: Pestaña; texto: string; activa: boolean }> = [
  { clave: 'usuarios', texto: 'Usuarios', activa: true },
  { clave: 'grupos', texto: 'Permisos por grupos', activa: true },
  { clave: 'oficinas', texto: 'Oficinas', activa: true },
  { clave: 'equipos', texto: 'Equipos', activa: true },
  // «Vacaciones (opción no activa de momento)».
  { clave: 'vacaciones', texto: 'Vacaciones', activa: false },
]

const VACIO = {
  id: 0,
  nombre: '',
  email: '',
  ocm_usuario: '',
  grupo_id: '',
  oficina_id: '',
  equipo_id: '',
  password: '',
  activo: true,
}

export function Personal({ usuario }: { usuario: Usuario }) {
  const clienteQuery = useQueryClient()
  const [pestaña, setPestaña] = useState<Pestaña>('usuarios')
  const [aviso, setAviso] = useState<string | null>(null)
  const [ficha, setFicha] = useState({ ...VACIO })
  const [grupoElegido, setGrupoElegido] = useState<number | null>(null)
  const [marcados, setMarcados] = useState<string[]>([])
  const [nombreNuevo, setNombreNuevo] = useState('')
  const [oficinaDelEquipo, setOficinaDelEquipo] = useState('')

  const datos = useQuery({
    queryKey: ['personal'],
    queryFn: () => api<DatosPersonal>('/api/personal/usuarios'),
    enabled: puede(usuario, 'personal.usuarios'),
  })

  const grupos = useQuery({
    queryKey: ['personal-grupos'],
    queryFn: () => api<{ grupos: Grupo[]; catalogo: RamaPermisos[] }>('/api/personal/grupos'),
    enabled: puede(usuario, 'personal.grupos'),
  })

  const refrescar = () => {
    void clienteQuery.invalidateQueries({ queryKey: ['personal'] })
    void clienteQuery.invalidateQueries({ queryKey: ['personal-grupos'] })
  }

  const guardar = useMutation({
    mutationFn: ({ ruta, cuerpo }: { ruta: string; cuerpo: Record<string, unknown> }) =>
      api(`/api/personal/${ruta}`, { metodo: 'POST', cuerpo }),
    onSuccess: () => {
      setAviso(null)
      setFicha({ ...VACIO })
      setNombreNuevo('')
      refrescar()
    },
    onError: (e) => setAviso(e instanceof ErrorApi ? e.message : 'No se ha podido guardar'),
  })

  const dominio = datos.data?.dominio ?? 'ecoasesores.es'
  const catalogo = grupos.data?.catalogo ?? []
  const grupo = grupos.data?.grupos.find((item) => item.id === grupoElegido) ?? null

  const elegirGrupo = (item: Grupo) => {
    setGrupoElegido(item.id)
    setMarcados(item.permisos)
  }

  // Un hijo no se puede marcar sin su padre, y al quitar el padre se van los hijos.
  const alternar = (clave: string) => {
    setMarcados((previos) => {
      if (previos.includes(clave)) {
        return previos.filter((p) => p !== clave && !p.startsWith(`${clave}.`))
      }

      const padre = clave.includes('.') ? clave.slice(0, clave.lastIndexOf('.')) : null
      const conPadre = padre && !previos.includes(padre) ? [...previos, padre] : previos

      return [...conPadre, clave]
    })
  }

  const editar = (persona: PersonaEnPersonal) =>
    setFicha({
      id: persona.id,
      nombre: persona.nombre,
      email: persona.email ?? '',
      ocm_usuario: persona.ocm_usuario ?? '',
      grupo_id: persona.grupo_id ? String(persona.grupo_id) : '',
      oficina_id: persona.oficina_id ? String(persona.oficina_id) : '',
      equipo_id: persona.equipo_id ? String(persona.equipo_id) : '',
      password: '',
      activo: persona.activo === 1,
    })

  const campo = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950'
  const etiqueta = 'block text-xs font-semibold text-slate-500'

  return (
    <div>
      <h1 className="mb-1 text-lg font-bold">Personal</h1>
      <p className="mb-4 text-sm text-slate-500">
        Quién entra, con qué permisos y en qué oficina y equipo.
      </p>

      <nav className="mb-4 flex flex-wrap gap-1">
        {PESTAÑAS.map((opcion) => (
          <button
            key={opcion.clave}
            onClick={() => opcion.activa && setPestaña(opcion.clave)}
            disabled={!opcion.activa}
            title={opcion.activa ? undefined : 'Todavía no'}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
              pestaña === opcion.clave
                ? 'bg-marca-700 text-white'
                : 'border border-slate-200 text-slate-600 disabled:opacity-40 dark:border-slate-700 dark:text-slate-300'
            }`}
          >
            {opcion.texto}
          </button>
        ))}
      </nav>

      {aviso && (
        <p className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{aviso}</p>
      )}

      {pestaña === 'usuarios' && (
        <div className="grid gap-5 xl:grid-cols-[1fr_340px]">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {datos.data?.usuarios.map((persona) => (
                <li key={persona.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-sm">
                  <span className={persona.activo ? 'font-semibold' : 'font-semibold text-slate-400 line-through'}>
                    {persona.nombre}
                  </span>
                  <span className="text-xs text-slate-400">{persona.email}</span>
                  {persona.grupo && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800">
                      {persona.grupo}
                    </span>
                  )}
                  {persona.oficina && <span className="text-xs text-slate-500">{persona.oficina}</span>}
                  {persona.equipo && <span className="text-xs text-slate-500">{persona.equipo}</span>}
                  {persona.ocm_usuario && (
                    <span className="text-xs text-slate-400">OCM: {persona.ocm_usuario}</span>
                  )}
                  <button
                    onClick={() => editar(persona)}
                    className="ml-auto rounded-lg border border-slate-200 px-3 py-1 text-xs font-medium transition hover:border-marca-500 dark:border-slate-700"
                  >
                    Editar
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <form
            className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
            onSubmit={(e) => {
              e.preventDefault()
              guardar.mutate({ ruta: 'usuarios', cuerpo: ficha })
            }}
          >
            <h2 className="text-sm font-bold">{ficha.id === 0 ? 'Usuario nuevo' : 'Editar usuario'}</h2>

            <label className={etiqueta}>
              Nombre
              <input
                value={ficha.nombre}
                onChange={(e) => setFicha({ ...ficha, nombre: e.target.value })}
                className={`mt-1 ${campo}`}
              />
            </label>

            <label className={etiqueta}>
              Correo (con él se entra)
              <input
                value={ficha.email}
                onChange={(e) => setFicha({ ...ficha, email: e.target.value })}
                placeholder={`nombre@${dominio}`}
                className={`mt-1 ${campo}`}
              />
              <span className="mt-1 block text-xs font-normal text-slate-400">
                Tiene que ser una cuenta @{dominio}.
              </span>
            </label>

            <label className={etiqueta}>
              Usuario de OCM
              <input
                value={ficha.ocm_usuario}
                onChange={(e) => setFicha({ ...ficha, ocm_usuario: e.target.value })}
                className={`mt-1 ${campo}`}
              />
              <span className="mt-1 block text-xs font-normal text-slate-400">
                Al guardarlo, sus llamadas ya importadas se le asignan solas.
              </span>
            </label>

            <label className={etiqueta}>
              Grupo
              <select
                value={ficha.grupo_id}
                onChange={(e) => setFicha({ ...ficha, grupo_id: e.target.value })}
                className={`mt-1 ${campo}`}
              >
                <option value="">— elegir —</option>
                {datos.data?.grupos.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.nombre}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className={etiqueta}>
                Oficina
                <select
                  value={ficha.oficina_id}
                  onChange={(e) => setFicha({ ...ficha, oficina_id: e.target.value })}
                  className={`mt-1 ${campo}`}
                >
                  <option value="">—</option>
                  {datos.data?.oficinas.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.nombre}
                    </option>
                  ))}
                </select>
              </label>

              <label className={etiqueta}>
                Equipo
                <select
                  value={ficha.equipo_id}
                  onChange={(e) => setFicha({ ...ficha, equipo_id: e.target.value })}
                  className={`mt-1 ${campo}`}
                >
                  <option value="">—</option>
                  {datos.data?.equipos.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.nombre}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className={etiqueta}>
              Contraseña
              <input
                type="password"
                value={ficha.password}
                onChange={(e) => setFicha({ ...ficha, password: e.target.value })}
                placeholder={ficha.id === 0 ? '' : 'solo si se cambia'}
                className={`mt-1 ${campo}`}
              />
            </label>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={ficha.activo}
                onChange={(e) => setFicha({ ...ficha, activo: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 accent-marca-700"
              />
              Activo
            </label>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={guardar.isPending}
                className="rounded-lg bg-marca-700 px-4 py-2 text-sm font-bold text-white transition hover:bg-marca-800 disabled:opacity-50"
              >
                Guardar
              </button>
              {ficha.id !== 0 && (
                <button
                  type="button"
                  onClick={() => setFicha({ ...VACIO })}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700"
                >
                  Nuevo
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {pestaña === 'grupos' && (
        <div className="grid gap-5 xl:grid-cols-[280px_1fr]">
          <div className="space-y-2">
            <ul className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
              {grupos.data?.grupos.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => elegirGrupo(item)}
                    className={`w-full px-4 py-2.5 text-left text-sm transition ${
                      grupoElegido === item.id ? 'bg-marca-50 font-semibold text-marca-700' : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    {item.nombre}
                    {item.es_admin === 1 && (
                      <span className="ml-2 text-xs font-normal text-slate-400">lo puede todo</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>

            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                guardar.mutate({ ruta: 'grupos', cuerpo: { nombre: nombreNuevo } })
              }}
            >
              <input
                value={nombreNuevo}
                onChange={(e) => setNombreNuevo(e.target.value)}
                placeholder="Grupo nuevo"
                className={campo}
              />
              <button className="rounded-lg border border-slate-200 px-3 text-sm dark:border-slate-700">+</button>
            </form>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            {grupo === null ? (
              <p className="text-sm text-slate-400">Elige un grupo para ver sus permisos.</p>
            ) : grupo.es_admin === 1 ? (
              <p className="text-sm text-slate-500">
                <strong>{grupo.nombre}</strong> lo puede todo, incluidos estos permisos. No hay nada
                que marcar.
              </p>
            ) : (
              <>
                <h2 className="mb-3 text-sm font-bold">{grupo.nombre}</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {catalogo.map((rama) => (
                    <div key={rama.clave}>
                      <label className="flex items-center gap-2 text-sm font-semibold">
                        <input
                          type="checkbox"
                          checked={marcados.includes(rama.clave)}
                          onChange={() => alternar(rama.clave)}
                          className="h-4 w-4 rounded border-slate-300 accent-marca-700"
                        />
                        {rama.etiqueta}
                      </label>
                      <ul className="mt-1 space-y-1 pl-6">
                        {rama.hijos.map((hijo) => (
                          <li key={hijo.clave}>
                            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                              <input
                                type="checkbox"
                                checked={marcados.includes(hijo.clave)}
                                onChange={() => alternar(hijo.clave)}
                                className="h-4 w-4 rounded border-slate-300 accent-marca-700"
                              />
                              {hijo.etiqueta}
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() =>
                    guardar.mutate({
                      ruta: 'grupos',
                      cuerpo: { id: grupo.id, nombre: grupo.nombre, permisos: marcados },
                    })
                  }
                  disabled={guardar.isPending}
                  className="mt-4 rounded-lg bg-marca-700 px-4 py-2 text-sm font-bold text-white transition hover:bg-marca-800 disabled:opacity-50"
                >
                  Guardar los permisos
                </button>
                <p className="mt-2 text-xs text-slate-400">
                  Si no se marca ningún estado, el grupo los ve todos. En cuanto se marca uno, solo
                  verá los marcados.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {pestaña === 'oficinas' && (
        <div className="max-w-xl space-y-3">
          <ul className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            {datos.data?.oficinas.map((item) => (
              <li key={item.id} className="px-4 py-2.5 text-sm">
                {item.nombre}
              </li>
            ))}
          </ul>

          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              guardar.mutate({ ruta: 'oficinas', cuerpo: { nombre: nombreNuevo } })
            }}
          >
            <input
              value={nombreNuevo}
              onChange={(e) => setNombreNuevo(e.target.value)}
              placeholder="Oficina nueva"
              className={campo}
            />
            <button className="rounded-lg bg-marca-700 px-4 text-sm font-bold text-white">Añadir</button>
          </form>
        </div>
      )}

      {pestaña === 'equipos' && (
        <div className="max-w-xl space-y-3">
          <ul className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            {datos.data?.equipos.map((item) => (
              <li key={item.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <span>{item.nombre}</span>
                {item.oficina ? (
                  <span className="text-xs text-slate-400">{item.oficina}</span>
                ) : (
                  <span className="text-xs text-amber-600">sin oficina</span>
                )}
              </li>
            ))}
          </ul>

          {/* Sin oficina, el supervisor no ve a ese equipo en sus métricas. */}
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              guardar.mutate({
                ruta: 'equipos',
                cuerpo: { nombre: nombreNuevo, oficina_id: oficinaDelEquipo },
              })
            }}
          >
            <input
              value={nombreNuevo}
              onChange={(e) => setNombreNuevo(e.target.value)}
              placeholder="Equipo nuevo"
              className={campo}
            />
            <select
              value={oficinaDelEquipo}
              onChange={(e) => setOficinaDelEquipo(e.target.value)}
              className={campo}
            >
              <option value="">— oficina —</option>
              {datos.data?.oficinas.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.nombre}
                </option>
              ))}
            </select>
            <button className="rounded-lg bg-marca-700 px-4 text-sm font-bold text-white">Añadir</button>
          </form>
        </div>
      )}
    </div>
  )
}
